/**
 * Cooling Mat � Buy 1 / 2 / 3 bundle offer
 * Isolated from Dawn cart.js; reuses cart drawer + PUB_SUB_EVENTS.cartUpdate.
 */

const CoolingMatBundleUtils = {
  findVariant(product, selectedOptions) {
    if (!product?.variants?.length) return null;

    const values = product.options.map((optionName, index) => {
      if (Array.isArray(selectedOptions)) return selectedOptions[index];
      return (
        selectedOptions[optionName] ??
        selectedOptions[optionName?.toLowerCase?.()] ??
        selectedOptions[`option${index + 1}`]
      );
    });

    if (values.some((value) => value == null || value === '')) return null;

    return (
      product.variants.find((variant) =>
        variant.options.every((optionValue, index) => optionValue === values[index])
      ) || null
    );
  },

  validateStock(variants) {
    const unavailable = variants.filter((variant) => !variant?.available);
    return {
      valid: unavailable.length === 0,
      unavailable,
    };
  },

  formatMoney(cents, moneyFormat) {
    const amount = (Number(cents) / 100).toFixed(2);
    const amountNoDecimals = String(Math.round(Number(cents) / 100));

    if (!moneyFormat) return `$${amount}`;

    return moneyFormat
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.replace('.', ','))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, amount);
  },
};

if (!customElements.get('cooling-mat-bundle')) {
  customElements.define(
    'cooling-mat-bundle',
    class CoolingMatBundle extends HTMLElement {
      constructor() {
        super();
        this.tier = 2;
        this.bundleQuantity = 1;
        this.selections = {};
        this.isSubmitting = false;
      }

      connectedCallback() {
        this.product = this.getProductData();
        if (!this.product) return;

        this.mediaMap = this.getMediaMap();

        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.buy2Discount = Number(this.dataset.buy2Discount) || 0;
        this.buy3Discount = Number(this.dataset.buy3Discount) || 0;
        this.tier = Number(this.dataset.defaultTier) || 2;
        this.sectionId = this.dataset.sectionId;
        this.preorder = this.dataset.preorder === 'true';

        this.errorEl = this.querySelector('[data-bundle-error]');
        this.submitButton = this.querySelector('[data-bundle-submit]');
        this.submitLabel = this.submitButton?.querySelector('[data-bundle-submit-label]');
        this.spinner = this.submitButton?.querySelector('.loading__spinner');
        this.bundleQuantityInput = this.querySelector('[data-bundle-quantity]');
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.bindTierClicks();
        this.bindSelectorChanges();
        this.bindQuantityInput();
        this.bindSubmit();
        this.bindVariantPickerSync();
        this.interceptProductFormSubmit();
        this.initSelectionsFromDom();
        this.setTier(this.tier, false);
      }

      initSelectionsFromDom() {
        this.querySelectorAll('[data-bundle-option]').forEach((select) => {
          const itemIndex = Number(select.dataset.itemIndex);
          const optionName = select.dataset.optionName;
          if (!this.selections[itemIndex]) this.selections[itemIndex] = {};
          if (select.value) this.selections[itemIndex][optionName] = select.value;
        });
      }

      getDefaultVariant() {
        return this.product.variants.find((variant) => variant.available) || this.product.variants[0];
      }

      hasCompleteSelections(selections) {
        if (!selections) return false;
        return this.product.options.every((optionName) => Boolean(selections[optionName]));
      }

      getSelectionsForItem(itemIndex) {
        if (this.hasCompleteSelections(this.selections[itemIndex])) {
          return { ...this.selections[itemIndex] };
        }

        if (itemIndex > 0 && this.hasCompleteSelections(this.selections[0])) {
          return { ...this.selections[0] };
        }

        const defaultVariant = this.getDefaultVariant();
        const selections = {};
        this.product.options.forEach((optionName, optionIndex) => {
          selections[optionName] = defaultVariant?.options?.[optionIndex] || '';
        });
        return selections;
      }

      interceptProductFormSubmit() {
        const productForm = document.querySelector(`product-form[data-section-id="${this.sectionId}"]`);
        if (!productForm?.form) return;

        this._formSubmitHandler = (event) => {
          if (this.tier === 1) return;

          event.preventDefault();
          event.stopImmediatePropagation();
          this.addBundleToCart();
        };

        productForm.form.addEventListener('submit', this._formSubmitHandler, true);
      }

      bindVariantPickerSync() {
        const variantSelects = document.querySelector(`variant-selects[data-section="${this.sectionId}"]`);
        if (!variantSelects) return;

        variantSelects.addEventListener('change', () => {
          if (this.tier !== 1) return;
          this.syncSelectionsFromVariantPicker();
          this.clearError();
        });
      }

      syncSelectionsFromVariantPicker() {
        const variantSelects = document.querySelector(`variant-selects[data-section="${this.sectionId}"]`);
        if (!variantSelects) return;

        if (!this.selections[0]) this.selections[0] = {};

        variantSelects.querySelectorAll('select[name^="options"]').forEach((select) => {
          const match = select.name.match(/^options\[(.+)\]$/);
          if (match) this.selections[0][match[1]] = select.value;
        });
      }

      syncVariantPickerFromSelections(itemIndex = 0) {
        const variantSelects = document.querySelector(`variant-selects[data-section="${this.sectionId}"]`);
        if (!variantSelects) return false;

        const selections = this.getSelectionsForItem(itemIndex);
        let changed = false;

        this.product.options.forEach((optionName) => {
          const select = variantSelects.querySelector(`select[name="options[${optionName}]"]`);
          if (!select || !selections[optionName]) return;
          if (select.value === selections[optionName]) return;
          select.value = selections[optionName];
          changed = true;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        });

        return changed;
      }

      async refreshDefaultVariantPrice(variant) {
        const productInfo = this.getProductInfoElement();
        const productUrl = productInfo?.dataset?.url;
        const resolvedVariant =
          variant ||
          this.product.variants.find(
            (entry) => entry.id === Number(document.querySelector(`#product-form-${this.sectionId} [name="id"]`)?.value)
          );

        if (!resolvedVariant?.id || !productUrl) return;

        try {
          const response = await fetch(`${productUrl}?section_id=${this.sectionId}&variant=${resolvedVariant.id}`);
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const source = html.getElementById(`price-${this.sectionId}`);
          const destination = document.getElementById(`price-${this.sectionId}`);

          if (source && destination) {
            destination.innerHTML = source.innerHTML;
          }
        } catch (error) {
          console.error('cooling-mat-bundle: failed to refresh variant price', error);
        }
      }

      updateTier1MainDisplay(itemIndex = 0) {
        const variant = CoolingMatBundleUtils.findVariant(this.product, this.getSelectionsForItem(itemIndex));
        if (!variant) return;

        this.updateVariantInputs(variant.id);
        this.updateMainProductMedia(itemIndex);
        this.refreshDefaultVariantPrice(variant);
        this.syncVariantPickerFromSelections(itemIndex);
      }

      updateBundleUiMode(tier) {
        const productInfo = this.getProductInfoElement();
        const infoContainer =
          productInfo?.querySelector('.product__info-container') || productInfo;

        if (infoContainer) {
          infoContainer.classList.toggle('cooling-mat-bundle-single', tier === 1);
          infoContainer.classList.toggle('cooling-mat-bundle-multi', tier > 1);
          infoContainer.dataset.bundleTier = String(tier);
        }

        this.classList.toggle('cooling-mat-bundle--tier-1', tier === 1);
        this.classList.toggle('cooling-mat-bundle--tier-multi', tier > 1);
      }

      getMediaMap() {
        const jsonEl = this.querySelector('[data-bundle-media-map]');
        if (!jsonEl) return this.product.media || [];

        try {
          return JSON.parse(jsonEl.textContent);
        } catch (error) {
          console.error('cooling-mat-bundle: invalid media map JSON', error);
          return this.product.media || [];
        }
      }

      getProductData() {
        const jsonEl = this.querySelector('[data-bundle-product-json]');
        if (!jsonEl) return null;

        try {
          return JSON.parse(jsonEl.textContent);
        } catch (error) {
          console.error('cooling-mat-bundle: invalid product JSON', error);
          return null;
        }
      }

      bindTierClicks() {
        this.querySelectorAll('[data-bundle-tier]').forEach((tierEl) => {
          tierEl.addEventListener('click', (event) => {
            if (event.target.closest('select') || event.target.closest('[data-bundle-quantity]')) return;
            this.setTier(Number(tierEl.dataset.bundleTier));
          });

          const radio = tierEl.querySelector('input[type="radio"]');
          radio?.addEventListener('change', () => {
            if (radio.checked) this.setTier(Number(tierEl.dataset.bundleTier));
          });
        });
      }

      bindSelectorChanges() {
        this.querySelectorAll('[data-bundle-option]').forEach((select) => {
          select.addEventListener('change', () => {
            const itemIndex = Number(select.dataset.itemIndex);
            const optionName = select.dataset.optionName;

            if (!this.selections[itemIndex]) this.selections[itemIndex] = {};
            this.selections[itemIndex][optionName] = select.value;

            this.refreshOptionAvailability();
            this.updateDisplayedPricing();
            if (this.tier === 1) {
              this.updateTier1MainDisplay(itemIndex);
            } else {
              this.updateMainProductPrice();
              this.updateMainProductMedia(itemIndex);
            }
            this.clearError();
          });
        });
      }

      bindQuantityInput() {
        if (!this.bundleQuantityInput) return;

        const onQtyChange = () => {
          const parsed = Number(this.bundleQuantityInput.value);
          this.bundleQuantity = Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : 1;
          this.bundleQuantityInput.value = String(this.bundleQuantity);
          this.updateDisplayedPricing();
        };

        this.bundleQuantityInput.addEventListener('change', onQtyChange);
        this.bundleQuantityInput.addEventListener('input', onQtyChange);
      }

      bindSubmit() {
        this.submitButton?.addEventListener('click', (event) => {
          event.preventDefault();
          this.addBundleToCart();
        });
      }

      setTier(tier, focusSelectors = true) {
        this.tier = tier;
        this.clearError();
        this.updateBundleUiMode(tier);

        this.querySelectorAll('[data-bundle-tier]').forEach((tierEl) => {
          const isSelected = Number(tierEl.dataset.bundleTier) === tier;
          tierEl.classList.toggle('is-selected', isSelected);
          const radio = tierEl.querySelector('input[type="radio"]');
          if (radio) radio.checked = isSelected;
        });

        this.ensureSelectionsForTier(tier);
        this.refreshOptionAvailability();
        this.updateDisplayedPricing();

        if (tier === 1) {
          this.updateTier1MainDisplay(0);
        } else {
          this.updateMainProductPrice();
          this.updateMainProductMedia(0);
        }

        if (focusSelectors && tier > 1) {
          const firstSelect = this.querySelector(`[data-bundle-tier="${tier}"] [data-bundle-option]`);
          firstSelect?.focus();
        }
      }

      ensureSelectionsForTier(tier) {
        const firstVariant = this.getDefaultVariant();

        for (let itemIndex = 0; itemIndex < tier; itemIndex += 1) {
          if (!this.hasCompleteSelections(this.selections[itemIndex])) {
            this.selections[itemIndex] = this.getSelectionsForItem(itemIndex);
          }

          if (!this.hasCompleteSelections(this.selections[itemIndex])) {
            this.selections[itemIndex] = {};
            this.product.options.forEach((optionName, optionIndex) => {
              this.selections[itemIndex][optionName] = firstVariant?.options?.[optionIndex] || '';
            });
          }

          this.querySelectorAll(
            `[data-bundle-tier="${tier}"] [data-item-index="${itemIndex}"][data-bundle-option]`
          ).forEach((select) => {
            const optionName = select.dataset.optionName;
            const value = this.selections[itemIndex][optionName];
            if (value) select.value = value;
          });
        }
      }

      getProductInfoElement() {
        return (
          document.querySelector(`#MainProduct-${this.sectionId}`) ||
          document.querySelector(`product-info[data-section="${this.sectionId}"]`) ||
          document.querySelector('product-info')
        );
      }

      getFeaturedMediaIdForVariant(variant) {
        if (!variant) return null;

        const mediaItems = this.mediaMap || this.product.media || [];
        const attachedMedia = mediaItems.find((media) => media.variant_ids?.includes(variant.id));
        if (attachedMedia) return attachedMedia.id;

        if (variant.featured_media?.id) return variant.featured_media.id;

        if (variant.featured_image?.src) {
          const matchedMedia = mediaItems.find(
            (media) =>
              media.preview_src === variant.featured_image.src ||
              media.preview_image?.src === variant.featured_image.src
          );
          if (matchedMedia) return matchedMedia.id;
        }

        return null;
      }

      updateMainProductMedia(itemIndex) {
        const variant = CoolingMatBundleUtils.findVariant(this.product, this.selections[itemIndex] || {});
        if (!variant) return;

        const mediaId = this.getFeaturedMediaIdForVariant(variant);
        const productInfo = this.getProductInfoElement();
        const mediaGallery = productInfo?.querySelector('media-gallery');

        if (mediaGallery && mediaId) {
          mediaGallery.setActiveMedia(`${this.sectionId}-${mediaId}`, true);
        }

        this.updateVariantInputs(variant.id);
      }

      updateVariantInputs(variantId) {
        if (!variantId) return;

        document
          .querySelectorAll(`#product-form-${this.sectionId} [name="id"], #product-form-installment-${this.sectionId} [name="id"]`)
          .forEach((input) => {
            input.value = variantId;
            input.disabled = false;
          });
      }

      updateMainProductPrice() {
        if (this.tier === 1) return;

        const totals = this.calculateTierTotals(this.tier, true);
        const priceRoot = document.getElementById(`price-${this.sectionId}`);
        if (!priceRoot) return;

        const formatted = CoolingMatBundleUtils.formatMoney(totals.discounted, this.moneyFormat);
        const formattedCompare = CoolingMatBundleUtils.formatMoney(totals.compareTotal, this.moneyFormat);
        const hasSale = totals.compareTotal > totals.discounted;

        const priceWrapper = priceRoot.querySelector('.price');
        const regularPriceEl = priceRoot.querySelector(
          '.price__regular .mf_actual-price, .price__regular .price-item--regular:not(s)'
        );
        const salePriceEl = priceRoot.querySelector(
          '.price__sale .mf_compare-price, .price__sale .price-item--sale.price-item--last'
        );
        const comparePriceEl = priceRoot.querySelector('.price__sale s.mf_price, .price__sale s.price-item--regular');
        const compareWrap = comparePriceEl?.closest('span');
        const savedAmountEl = priceRoot.querySelector('.price__saved--amount');
        const savedWrap = priceRoot.querySelector('.price__saved');

        if (priceWrapper) {
          priceWrapper.classList.toggle('price--on-sale', hasSale);
        }

        // Dawn/MFF theme displays .price__sale by default; update both blocks.
        if (regularPriceEl) regularPriceEl.innerHTML = formatted;
        if (salePriceEl) salePriceEl.innerHTML = formatted;

        if (hasSale) {
          if (comparePriceEl) comparePriceEl.innerHTML = formattedCompare;
          if (compareWrap) compareWrap.style.display = '';

          if (savedAmountEl && savedWrap) {
            savedAmountEl.innerHTML = `Dein Rabatt ${totals.effectiveDiscountPercent}% (-${CoolingMatBundleUtils.formatMoney(
              totals.savedCents,
              this.moneyFormat
            )})`;
            savedWrap.classList.remove('hidden');
          }
        } else {
          if (compareWrap) compareWrap.style.display = 'none';
          savedWrap?.classList.add('hidden');
        }
      }

      getVariantsForTier(tier, useFallbacks = false) {
        const variants = [];

        for (let itemIndex = 0; itemIndex < tier; itemIndex += 1) {
          const selections = useFallbacks
            ? this.getSelectionsForItem(itemIndex)
            : this.selections[itemIndex] || this.getSelectionsForItem(itemIndex);

          variants.push(CoolingMatBundleUtils.findVariant(this.product, selections));
        }

        return variants;
      }

      getDiscountPercent(tier) {
        if (tier === 2) return this.buy2Discount;
        if (tier === 3) return this.buy3Discount;
        return 0;
      }

      getVariantCompareCents(variant) {
        const salePrice = variant?.price || 0;
        const compareAt = variant?.compare_at_price || 0;
        return compareAt > salePrice ? compareAt : salePrice;
      }

      calculateTierTotals(tier, includeBundleQuantity = true) {
        const variants = this.getVariantsForTier(tier, true);
        const subtotalSale = variants.reduce((sum, variant) => sum + (variant?.price || 0), 0);
        const compareTotalSingle = variants.reduce(
          (sum, variant) => sum + this.getVariantCompareCents(variant),
          0
        );
        const bundleDiscountPercent = this.getDiscountPercent(tier);
        const discountedSingle = Math.round(subtotalSale * (1 - bundleDiscountPercent / 100));
        const quantityMultiplier = includeBundleQuantity ? this.bundleQuantity : 1;

        const subtotal = subtotalSale * quantityMultiplier;
        const discounted = discountedSingle * quantityMultiplier;
        const compareTotal = compareTotalSingle * quantityMultiplier;
        const savedCents = Math.max(0, compareTotal - discounted);
        const effectiveDiscountPercent =
          compareTotal > 0 ? Math.round((savedCents / compareTotal) * 100) : 0;

        return {
          variants,
          subtotal,
          discounted,
          compareTotal,
          savedCents,
          effectiveDiscountPercent,
          bundleDiscountPercent,
          discountPercent: bundleDiscountPercent,
          subtotalSingle: subtotalSale,
          discountedSingle,
          compareTotalSingle,
        };
      }

      updateDisplayedPricing() {
        [1, 2, 3].forEach((tier) => {
          const totals = this.calculateTierTotals(tier, false);
          const priceEl = this.querySelector(`[data-tier-price="${tier}"]`);
          const compareEl = this.querySelector(`[data-tier-compare="${tier}"]`);
          const saveEl = this.querySelector(`[data-tier-save="${tier}"]`);

          if (priceEl) {
            priceEl.innerHTML = CoolingMatBundleUtils.formatMoney(totals.discounted, this.moneyFormat);
          }

          if (compareEl) {
            if (totals.compareTotal > totals.discounted) {
              compareEl.innerHTML = CoolingMatBundleUtils.formatMoney(totals.compareTotal, this.moneyFormat);
              compareEl.hidden = false;
            } else {
              compareEl.hidden = true;
            }
          }

          // if (saveEl) {
          //   if (totals.effectiveDiscountPercent > 0) {
          //     saveEl.textContent = `Save Extra ${totals.effectiveDiscountPercent}%`;
          //     saveEl.hidden = false;
          //   } else {
          //     saveEl.hidden = true;
          //   }
          // }
          if (saveEl) {
  if (totals.bundleDiscountPercent > 0) {
    saveEl.textContent = `-${totals.bundleDiscountPercent}% zusätzlich`;
    saveEl.hidden = false;
  } else {
    saveEl.hidden = true;
  }
}
        });

        if (this.tier > 1) {
          this.updateMainProductPrice();
        }
      }

      refreshOptionAvailability() {
        const tier = this.tier;

        for (let itemIndex = 0; itemIndex < tier; itemIndex += 1) {
          this.querySelectorAll(
            `[data-bundle-tier="${tier}"] [data-item-index="${itemIndex}"][data-bundle-option]`
          ).forEach((select) => {
            const optionIndex = Number(select.dataset.optionIndex);
            const currentSelections = { ...(this.selections[itemIndex] || {}) };

            Array.from(select.options).forEach((option) => {
              if (!option.value) return;

              const testSelections = { ...currentSelections, [select.dataset.optionName]: option.value };
              const variant = CoolingMatBundleUtils.findVariant(this.product, testSelections);
              const optionAvailable = this.isOptionValueAvailable(testSelections, optionIndex, option.value);

              option.disabled = !optionAvailable || !variant?.available;
            });

            const selectedOption = select.options[select.selectedIndex];
            if (selectedOption?.disabled) {
              const firstAvailable = Array.from(select.options).find((option) => !option.disabled && option.value);
              if (firstAvailable) {
                select.value = firstAvailable.value;
                this.selections[itemIndex][select.dataset.optionName] = firstAvailable.value;
              }
            }
          });
        }

        this.updateSubmitState();
      }

      isOptionValueAvailable(selections, optionIndex, optionValue) {
        return this.product.variants.some((variant) => {
          if (!variant.available) return false;
          return this.product.options.every((optionName, index) => {
            const selected = index === optionIndex ? optionValue : selections[optionName];
            if (!selected) return true;
            return variant.options[index] === selected;
          });
        });
      }

      updateSubmitState() {
        if (!this.submitButton) return;

        const variants = this.getVariantsForTier(this.tier, false);
        const stock = CoolingMatBundleUtils.validateStock(variants);
        const shouldDisable = !stock.valid || this.isSubmitting;

        this.submitButton.toggleAttribute('aria-disabled', shouldDisable);
        this.submitButton.disabled = shouldDisable;

        if (!stock.valid) {
          this.submitLabel.textContent = window.variantStrings?.soldOut || 'Sold out';
        } else if (this.preorder) {
          this.submitLabel.textContent = 'Pre-order';
        } else {
          this.submitLabel.textContent = window.variantStrings?.addToCart || 'Add to cart';
        }
      }

      buildItemsPayload() {
        const variants = this.getVariantsForTier(this.tier, false);

        return variants.map((variant) => ({
          id: variant.id,
          quantity: this.bundleQuantity,
          properties: {
            _bundle_tier: String(this.tier),
            _bundle_source: 'cooling-mat-bundle',
          },
        }));
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.classList.add('is-visible');
      }

      clearError() {
        if (!this.errorEl) return;
        this.errorEl.textContent = '';
        this.errorEl.classList.remove('is-visible');
      }

      setLoading(isLoading) {
        this.isSubmitting = isLoading;
        this.submitButton?.classList.toggle('loading', isLoading);
        this.spinner?.classList.toggle('hidden', !isLoading);
        this.updateSubmitState();
      }

      async addBundleToCart() {
        if (this.isSubmitting || this.submitButton?.getAttribute('aria-disabled') === 'true') return;

        const variants = this.getVariantsForTier(this.tier, false);
        const stock = CoolingMatBundleUtils.validateStock(variants);

        if (!stock.valid) {
          this.showError(
            window.variantStrings?.unavailable ||
              'One or more selected options are unavailable. Please choose a different combination.'
          );
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'bundle-offer',
            errors: 'Unavailable variants',
            message: 'Bundle contains unavailable variants',
          });
          return;
        }

        this.clearError();
        this.setLoading(true);

        const items = this.buildItemsPayload();
        const sections = this.cart?.getSectionsToRender?.().map((section) => section.id) || [
          'cart-drawer',
          'cart-icon-bubble',
        ];

        const body = {
          items,
          sections: sections.join(','),
          sections_url: window.location.pathname,
        };

        try {
          const response = await fetch(routes.cart_add_url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify(body),
          });

          const parsedState = await response.json();

          if (parsedState.status) {
            const message = parsedState.description || parsedState.message || window.cartStrings?.error;
            this.showError(message);
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'bundle-offer',
              errors: parsedState.errors || parsedState.description,
              message: parsedState.message,
            });
            return;
          }

          const startMarker = CartPerformance?.createStartingMarker?.('add:wait-for-subscribers');
          await publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'bundle-offer',
            cartData: parsedState,
          });
          if (startMarker) {
            CartPerformance?.measureFromMarker?.('add:wait-for-subscribers', startMarker);
          }

          if (this.cart) {
            CartPerformance?.measure?.('add:paint-updated-sections', () => {
              this.cart.renderContents(parsedState);
            });
            if (this.cart.classList.contains('is-empty')) {
              this.cart.classList.remove('is-empty');
            }
          }
        } catch (error) {
          console.error('cooling-mat-bundle add error', error);
          this.showError(window.cartStrings?.error || 'Could not add items to cart. Please try again.');
        } finally {
          this.setLoading(false);
        }
      }
    }
  );
}

window.CoolingMatBundleUtils = CoolingMatBundleUtils;
